import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Beaker, Users, TrendingDown, AlertTriangle, ExternalLink, BookOpen } from 'lucide-react';

const GreenTeaResearchPage = () => {
  return (
    <div className="space-y-8">
      {/* Back Button */}
      <Link
        to="/integrative-health/supplements"
        className="inline-flex items-center text-slate-600 hover:text-slate-900 transition-colors"
        data-testid="back-to-supplements-link"
      >
        <ArrowLeft className="h-5 w-5 mr-2" />
        Back to Supplements Library
      </Link>

      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 bg-green-100 rounded-xl">
            <Beaker className="h-8 w-8 text-green-600" />
          </div>
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900" data-testid="page-title">
              Green Tea Extract (EGCG) & CLL
            </h1>
            <p className="text-slate-600">Research from Mayo Clinic</p>
          </div>
        </div>
      </div>

      {/* Research Highlight Box */}
      <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-6 md:p-8">
        <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">
          Mayo Clinic Phase II Clinical Trial
        </h2>
        
        <div className="space-y-4 text-base md:text-lg text-slate-700 leading-relaxed">
          <p>
            In 2010, researchers at Mayo Clinic published findings showing that an extract of green tea appears to have clinical activity with low toxicity in CLL patients who used it in a phase II clinical trial.
          </p>
          
          <p>
            The study focused on <strong>epigallocatechin gallate (EGCG)</strong> — the major active component of green tea. Mayo Clinic first tested EGCG in laboratory assays about eight years prior, finding it reduced the survival of CLL leukaemia cells. This laboratory discovery led to clinical trials in actual patients.
          </p>
        </div>
      </div>

      {/* Key Findings */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
          <TrendingDown className="h-7 w-7 text-green-600" />
          Key Findings
        </h2>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <div className="text-4xl font-bold text-green-600 mb-2">69%</div>
            <p className="text-slate-700">
              of CLL patients had a biological response to EGCG, showing either a 20% or greater sustained reduction in blood lymphocyte count and/or a 50% or greater reduction in lymph node size.
            </p>
          </div>
          
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <div className="text-4xl font-bold text-green-600 mb-2">31%</div>
            <p className="text-slate-700">
              of patients achieved a 20% or greater sustained reduction in their blood leukaemia cell count.
            </p>
          </div>
          
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <div className="text-4xl font-bold text-green-600 mb-2">69%</div>
            <p className="text-slate-700">
              of patients who entered the study with enlarged lymph nodes saw a 50% or greater reduction in lymph node size.
            </p>
          </div>
          
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <div className="text-4xl font-bold text-slate-600 mb-2">42</div>
            <p className="text-slate-700">
              patients participated in the combined phase I and phase II trials at Mayo Clinic.
            </p>
          </div>
        </div>
      </div>

      {/* Researcher Quotes */}
      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6 md:p-8">
        <h2 className="text-xl font-semibold text-blue-900 mb-6 flex items-center gap-3">
          <Users className="h-6 w-6" />
          What the Researchers Said
        </h2>
        
        <div className="space-y-6">
          <blockquote className="border-l-4 border-blue-400 pl-4">
            <p className="text-slate-700 italic mb-2">
              "Although only a comparative phase III trial can determine whether EGCG can delay progression of CLL, the benefits we have seen in most CLL patients who use the chemical suggest that it has modest clinical activity and may be useful for stabilising this form of leukaemia, potentially slowing it down."
            </p>
            <cite className="text-sm text-slate-600 not-italic">
              — Dr. Tait Shanafelt, Mayo Clinic haematologist and lead author
            </cite>
          </blockquote>
          
          <blockquote className="border-l-4 border-blue-400 pl-4">
            <p className="text-slate-700 italic mb-2">
              "These studies advance the notion that a nutraceutical like EGCG can and should be studied as cancer preventives. Using non-toxic chemicals to push back cancer growth to delay the need for toxic therapies is a worthy goal in oncology research — particularly for forms of cancer initially managed by observation such as CLL."
            </p>
            <cite className="text-sm text-slate-600 not-italic">
              — Dr. Neil Kay, haematology researcher who first tested green tea extract in leukaemic cells from CLL patients
            </cite>
          </blockquote>
        </div>
      </div>

      {/* Important Context */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
          <BookOpen className="h-7 w-7 text-slate-600" />
          Important Context
        </h2>
        
        <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
          <div>
            <h3 className="font-semibold text-slate-900 mb-2">Who was studied?</h3>
            <p className="text-slate-700">
              All patients in the trial were <strong>early stage, asymptomatic CLL patients</strong> who would not otherwise be treated until their disease progressed. This is the "watch and wait" population.
            </p>
          </div>
          
          <div>
            <h3 className="font-semibold text-slate-900 mb-2">What was the EGCG source?</h3>
            <p className="text-slate-700">
              The extract was supplied by the National Cancer Institute (NCI) and Polyphenon E International for these clinical trials. This was a standardised, pharmaceutical-grade preparation, not an over-the-counter supplement.
            </p>
          </div>
          
          <div>
            <h3 className="font-semibold text-slate-900 mb-2">Side effects</h3>
            <p className="text-slate-700">
              The treatment was well tolerated with very mild side effects in most patients. Only 3 of 42 patients experienced a grade 3 side effect during the six months of treatment.
            </p>
          </div>
          
          <div>
            <h3 className="font-semibold text-slate-900 mb-2">What this means</h3>
            <p className="text-slate-700">
              CLL is managed by measuring leukaemia cells in blood and bone marrow, and monitoring lymph node enlargement. The study showed EGCG may help reduce both of these markers in many patients.
            </p>
          </div>
        </div>
      </div>

      {/* Important Warning */}
      <div className="bg-amber-50 border border-amber-300 rounded-xl p-6" data-testid="warning-box">
        <div className="flex items-start gap-4">
          <AlertTriangle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-1" />
          <div>
            <h3 className="font-semibold text-amber-900 mb-3">Important Considerations</h3>
            <ul className="space-y-2 text-amber-800">
              <li className="flex items-start gap-2">
                <span className="text-amber-600 mt-1">•</span>
                <span><strong>EGCG is not a substitute for chemotherapy.</strong> The researchers were clear about this distinction.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 mt-1">•</span>
                <span><strong>No phase III trial has been completed.</strong> Without this, researchers cannot make formal recommendations for CLL patients to use EGCG.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 mt-1">•</span>
                <span><strong>Over-the-counter supplements differ from trial preparations.</strong> Quality, concentration, and purity vary significantly between products.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 mt-1">•</span>
                <span><strong>If you want to try EGCG supplements, consult your oncologist first</strong> and ensure you receive appropriate monitoring using laboratory tests.</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Source */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-6">
        <h3 className="font-semibold text-slate-900 mb-3">Source</h3>
        <p className="text-slate-700 mb-3">
          This information is based on research presented at the American Society of Clinical Oncology (ASCO) annual meeting in June 2010.
        </p>
        <a 
          href="https://newsnetwork.mayoclinic.org/discussion/green-tea-extract-appears-to-keep-cancer-in-check-in-majority-of-cll-patients/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors"
        >
          <ExternalLink className="h-4 w-4" />
          Read the original Mayo Clinic article
        </a>
        <p className="text-sm text-slate-500 mt-2">
          Study funded by grants from the NCI, Mayo Comprehensive Cancer Centre, and from donors and patient advocacy foundations.
        </p>
      </div>

      {/* Related Links */}
      <div className="flex flex-col sm:flex-row gap-4 pt-4">
        <Link
          to="/integrative-health/supplements"
          className="flex-1 text-center px-6 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors font-medium"
        >
          View All Supplements
        </Link>
        <Link
          to="/integrative-health/food-nutrition"
          className="flex-1 text-center px-6 py-3 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-colors font-medium border border-slate-200"
        >
          Food & Nutrition
        </Link>
      </div>
    </div>
  );
};

export default GreenTeaResearchPage;
